// Surface styling for the two dashboard banners.
//
// Values come straight from Figma (Copy as CSS), including the odd-looking
// 105deg angle and fractional colour stops — those are the design's numbers, not
// rounding artefacts, so do not "tidy" them.
//
// Each banner wears THREE overlapping edge treatments and needs all of them:
//   inset ring  1px translucent line just inside the radius
//   outer ring  1px solid line just outside it (the bright outline you actually see)
//   glow        a wide soft halo
// Dropping the outer ring or the glow reads as a noticeably flatter banner.
//
// All three are box-shadows; none is a CSS `border`. Figma calls that stroke
// "inner alignment", which is an inset stroke painted OVER the fill — a real
// border is a separate box region instead, and any rule that stops the background
// short of it (Bootstrap is global here) leaves a dark seam wherever the fill is
// bright, which is exactly what showed up along the right edge. An inset shadow
// cannot desync from the fill that way, and it also keeps the border box the size
// the design specified rather than eating 1px per side.

import { BRAND_PALETTE, isValidHex } from 'constants/brand-palette'

export interface BannerSurface {
  background: string
  boxShadow: string
  borderRadius: string
}

const GRADIENT_ANGLE = '105deg'
const STOP_DARK = '#0D1320 7.74%'
const STOP_MID = '#141C2F 41.55%'
const RADIUS = '16px'

/**
 * Player banner accent — fixed, never derived from an Organization.
 * Exported because the banner's own text picks it up (the position word and the
 * separator dot are the same purple as the surface).
 */
export const PLAYER_ACCENT = '#A855F7'

/** Fallback when an Organization carries no valid colour of its own. */
const FALLBACK_ACCENT = BRAND_PALETTE[0].hex

/**
 * `#RRGGBB` + alpha → `rgba(r, g, b, a)`.
 * Invalid input falls back rather than returning an empty string: an empty value
 * spliced into `linear-gradient(...)` invalidates the whole declaration and the
 * banner loses its background entirely.
 */
export const hexToRgba = (hex: string, alpha: number): string => {
  const safe = isValidHex(hex) ? hex : FALLBACK_ACCENT
  const r = parseInt(safe.slice(1, 3), 16)
  const g = parseInt(safe.slice(3, 5), 16)
  const b = parseInt(safe.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Player banner: purple, identical for every player.
 * The accent stop is FULLY OPAQUE and the ring is solid — that is why this banner
 * reads far more vivid than the organization one. Intentional; see buildOrgSurface.
 */
export const buildPlayerSurface = (): BannerSurface => ({
  background: `linear-gradient(${GRADIENT_ANGLE}, ${STOP_DARK}, ${STOP_MID}, ${PLAYER_ACCENT} 92.26%)`,
  boxShadow: [
    `inset 0px 0px 0px 1px ${hexToRgba(PLAYER_ACCENT, 0.125)}`,
    `0px 0px 0px 1px ${PLAYER_ACCENT}`,
    `0px 8px 40px ${hexToRgba(PLAYER_ACCENT, 0.35)}`
  ].join(', '),
  borderRadius: RADIUS
})

/**
 * The accent an organization banner will actually use, after validation.
 *
 * Exported so callers can paint sibling details — a label, a logo border — in the
 * same colour without parsing it back out of the CSS strings below.
 */
export const resolveOrgAccent = (primaryColor?: string): string =>
  isValidHex(primaryColor) ? primaryColor : FALLBACK_ACCENT

/**
 * Organization / Team banner, tinted by the org's accent.
 *
 * The accent lands at low alpha throughout (15% gradient stop, 25% border, 10%
 * ring, 20% glow) so the banner stays a dark surface with a wash of colour rather
 * than a block of it. Deliberately softer than the player banner — matching the
 * two would flatten the visual hierarchy the design sets up.
 */
export const buildOrgSurface = (primaryColor?: string): BannerSurface => {
  const accent = resolveOrgAccent(primaryColor)
  return {
    // Figma's exported gradient, verbatim — same angle and stops as the player
    // banner, differing only in that the accent stop is translucent.
    background: `linear-gradient(${GRADIENT_ANGLE}, ${STOP_DARK}, ${STOP_MID}, ${hexToRgba(accent, 0.15)} 92.26%)`,
    boxShadow: [
      `inset 0px 0px 0px 1px ${hexToRgba(accent, 0.25)}`,
      `0px 0px 0px 1px ${hexToRgba(accent, 0.1)}`,
      `0px 8px 40px ${hexToRgba(accent, 0.2)}`
    ].join(', '),
    borderRadius: RADIUS
  }
}
